import React, { useEffect, useState } from "react";
import { RightOutlined } from "@ant-design/icons";
import { Collapse, Form, FormInstance, Tabs } from "antd";
import Loading from "components/loading";
import { ILocationData } from "store/location/location-model";
import { IForm } from "store/readers-tags/readers/readers-model";
import theme from "styles/theme";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "../index.styled";
import { accessPointOptionsReader } from "../types";
import FormAntenna from "./antenna";
import FormReader from "./reader";

export interface IPropsFormReader {
  form: IForm;
  formReader: FormInstance;
  formAntenna: FormInstance;
  locationActiveList: ILocationData[];
  isLoadingForm: boolean;
}

interface IDispatchProps {
  locationActiveFetchAsync: () => void;
  readersUpdateFormReader: (...args) => void;
  readersUpdateFormAntenna: (...args) => void;
  readersRemoveAntenna: (...args) => void;
}

type ExpandIconProps = { isActive: boolean };
const ExpandIcon: React.FC<ExpandIconProps> = ({ isActive }) => <RightOutlined rotate={isActive ? 90 : 270} />;

export type IFormProps = IPropsFormReader & IDispatchProps;

const FormReaderAntenna = ({
  form,
  formReader,
  formAntenna,
  isLoadingForm,
  locationActiveList,
  readersRemoveAntenna,
  readersUpdateFormReader,
  readersUpdateFormAntenna,
  locationActiveFetchAsync,
}: IFormProps) => {
  const [activeCollapseKeys, setActiveCollapseKeys] = useState<string[]>([]);
  const READER_REGISTER = useTranslation({ id: "reader_register" });
  const READER_EDIT = useTranslation({ id: "reader_edit" });
  const ANTENNA_EDIT = useTranslation({ id: "antennas_edit" });
  const ANTENNAS_REGISTER = useTranslation({ id: "antennas_register" });
  const ANTENNA_TITLE = useTranslation({ id: "antenna" });

  useEffect(() => {
    locationActiveFetchAsync();
  }, [locationActiveFetchAsync]);

  useEffect(() => {
    if (!formReader || !formAntenna) return;
    if (!accessPointOptionsReader.length) return;

    if (form.reader.id) {
      formReader.setFieldsValue(form.reader);
    }

    const description = accessPointOptionsReader.find((item) => item.id === form.reader.readerPositionType)?.description || "";

    const antennaSlots = Array(4)
      .fill(null)
      .map((_, index) => {
        const antenna = form.antennas[index] || {};

        return {
          _slotIndex: index,
          ...antenna,
          modelDescription: description,
        };
      });

    formAntenna.setFieldsValue({ antennas: antennaSlots });
  }, [form, accessPointOptionsReader, formReader, formAntenna]);

  const antennaHasError = (index: number) => {
    const fields = ["antennaNumber", "antennaPositionType", "flow", "antennaPort"];

    return fields.some((field) => formAntenna.getFieldError(["antennas", index, field]).length > 0);
  };

  return (
    <Loading show={isLoadingForm}>
      <S.FormContainer>
        <Tabs defaultActiveKey="1" style={{ height: "100%", width: "100%" }} onChange={() => setActiveCollapseKeys([])} className="full-width-tabs">
          <Tabs.TabPane tab={form.reader.id ? READER_EDIT : READER_REGISTER} key="1" data-testid="tab-data" style={{ height: "100%" }}>
            <FormReader
              formReader={formReader}
              formAntenna={formAntenna}
              locationActiveList={locationActiveList}
              readersUpdateFormReader={readersUpdateFormReader}
              readersUpdateFormAntenna={readersUpdateFormAntenna}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab={form.reader.id ? ANTENNA_EDIT : ANTENNAS_REGISTER} key="2" data-testid="tab-items">
            <Form
              layout="vertical"
              style={{ width: "100%" }}
              form={formAntenna}
              initialValues={{
                antennas: [{ _slotIndex: 0 }, { _slotIndex: 1 }, { _slotIndex: 2 }, { _slotIndex: 3 }],
              }}
              onValuesChange={(_, allValues) => readersUpdateFormAntenna(allValues)}
            >
              <Form.List name="antennas">
                {(fields) => (
                  <Collapse
                    ghost
                    expandIconPosition="end"
                    activeKey={activeCollapseKeys}
                    expandIcon={ExpandIcon}
                    destroyInactivePanel={false}
                    onChange={(keys) => setActiveCollapseKeys(keys as string[])}
                  >
                    {fields.map((field, index) => (
                      <Collapse.Panel
                        key={String(field.key)}
                        header={
                          <span style={{ color: antennaHasError(index) ? theme["@color-danger-300"] : undefined }}>
                            {ANTENNA_TITLE} {index + 1}
                          </span>
                        }
                        style={{ fontSize: "20px", fontWeight: 600 }}
                      >
                        <FormAntenna
                          field={field}
                          readersRemoveAntenna={readersRemoveAntenna}
                          formAntenna={formAntenna}
                          collapseKey={String(field.key)}
                          setActiveCollapseKeys={setActiveCollapseKeys}
                        />
                      </Collapse.Panel>
                    ))}
                  </Collapse>
                )}
              </Form.List>
            </Form>
          </Tabs.TabPane>
        </Tabs>
      </S.FormContainer>
    </Loading>
  );
};

export default FormReaderAntenna;
